import { FC } from "react";
import { useFormContext } from "react-hook-form";
import { useGetLoggedInUserDetails } from "../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  DropDownData,
  IdNameList,
} from "../../../../../api/services/common/models";
import { DeliveryOfficeListItem } from "../../../../../api/services/mdmOffice/models";
import {
  FormController,
  MuiGrid,
  MuiSelect,
  MuiSelectPhoneWithTextInput,
  OptionsDataType,
  OptionsDataTypeWithNode,
} from "../../../../../components";
import { commonSiteCoreKeys } from "../../../../../helpers/constants/siteCoreKeys";
import { UserDetails } from "../../../../../helpers/types/common";
import {
  mapDropdownDataWithName,
  setApiDataToDropDownOption,
} from "../../../../../helpers/utils";
import { useGridContainer } from "../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { ProgramSearchProgramFormKey } from "../../../modelsAndSchemas/models";
import { programSearchSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";

interface GenericParticipantMoreFieldsProps {
  pageId: string;
  nextProgramClasses: IdNameList[];
  resetForm: boolean;
  countryListData: DropDownData[];
  handleCountryChange: (countryId: string) => void;
  deliveryOfficeListData: DeliveryOfficeListItem[];
  requestedByList: UserDetails[];
}
export const GenericParticipantMoreFields: FC<
  GenericParticipantMoreFieldsProps
> = ({
  pageId,
  nextProgramClasses,
  resetForm,
  countryListData,
  handleCountryChange,
  deliveryOfficeListData,
  requestedByList,
}) => {
  const { gridSizeLarge: gridSize, gridSizeLargeClass } = useGridContainer();
  const { control, getValues } = useFormContext();
  const {
    commonLabels,
    useFetchLabel,
    fetchValidationMessage,
    fetchDropdownValues,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { loggedInUser } = useGetLoggedInUserDetails();

  const getCountryIsoCode = () => {
    return loggedInUser?.countryCode;
  };

  const getLabel = useFetchLabel(programSearchSiteCoreKeys.searchFormKey);
  return (
    <MuiGrid container className="form-container">
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.nextProgramClass}
        >
          <MuiSelect
            id={`${pageId}-${ProgramSearchProgramFormKey.nextProgramClass}`}
            label={getLabel(programSearchSiteCoreKeys.nextProgramClass)}
            visibleDefaultSelectOption
            options={setApiDataToDropDownOption(nextProgramClasses)}
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.participantStatus}
        >
          <MuiSelect
            label={getLabel(programSearchSiteCoreKeys.participantStatus)}
            options={fetchDropdownValues(
              programSearchSiteCoreKeys.searchFormKey,
              programSearchSiteCoreKeys.participantStatusList,
            )}
            visibleDefaultSelectOption
            value=""
            id={`${pageId}-${ProgramSearchProgramFormKey.participantStatus}`}
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.participantMobileNumber}
        >
          <MuiSelectPhoneWithTextInput
            ignoreError
            label={getLabel(programSearchSiteCoreKeys.participantMobileNumber)}
            defaultValue={getValues(
              ProgramSearchProgramFormKey.participantMobileNumber,
            )}
            defaultSelectedCountry={getCountryIsoCode()}
            placeholder={commonLabels.telephonePlaceHolder}
            phoneNumberFormId={
              ProgramSearchProgramFormKey.participantMobileNumber
            }
            countryCodeFormId={ProgramSearchProgramFormKey.mobileCountryCode}
            countryIsoCodeFormId={ProgramSearchProgramFormKey.mobileISOCode}
            reset={resetForm}
            withSelectOption
            searchPlaceholder={commonLabels.search}
            searchNotFound={commonLabels.noResult}
            selectLabel={commonLabels.selectPlaceHolder}
            invalidPhoneNumberMessage={fetchValidationMessage(
              commonSiteCoreKeys.invalidPhoneNumber,
            )}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.purpose}
        >
          <MuiSelect
            id={`${pageId}-${ProgramSearchProgramFormKey.purpose}`}
            label={getLabel(programSearchSiteCoreKeys.purposeLabel)}
            visibleDefaultSelectOption
            options={fetchDropdownValues(
              programSearchSiteCoreKeys.searchFormKey,
              programSearchSiteCoreKeys.purposeList,
            )}
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.country}
        >
          <MuiSelect
            id={`${pageId}-${ProgramSearchProgramFormKey.country}`}
            label={getLabel(programSearchSiteCoreKeys.countrySearchLabel)}
            visibleDefaultSelectOption
            onChange={(e) => handleCountryChange(e.target.value as string)}
            options={setApiDataToDropDownOption(countryListData, true)}
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
            showSearch
            searchPlaceholder={commonLabels.search}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.deliveryOffice}
        >
          <MuiSelect
            id={`${pageId}-${ProgramSearchProgramFormKey.deliveryOffice}`}
            label={getLabel(programSearchSiteCoreKeys.deliveryOffices)}
            visibleDefaultSelectOption
            options={setApiDataToDropDownOption(deliveryOfficeListData)}
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.requestedBy}
        >
          <MuiSelect
            id={`${pageId}-${ProgramSearchProgramFormKey.requestedBy}`}
            label={getLabel(programSearchSiteCoreKeys.requestedBy)}
            options={mapDropdownDataWithName(requestedByList, true)}
            visibleDefaultSelectOption
            placeholder={commonLabels.selectPlaceHolder}
            searchPlaceholder={commonLabels.search}
            showSearch
            getSubText={(option: OptionsDataType | OptionsDataTypeWithNode) =>
              option.email
            }
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
    </MuiGrid>
  );
};
